import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import './PurchaseCredits.css';
import Title from 'antd/lib/typography/Title';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Icon } from 'antd';
import { CURRENCIES } from '../../../utility/via-util';
import BrainTreeDropIn from './BrainTreeDropIn';
import { requestPackagePurchase } from '../../../state/ui/purchase/purchaseCreditsUiActions';
import { selectChosenCreditPackage } from '../../../state/ui/purchase/purchaseCreditsUiReducer';
import { tokenPackageShape } from './purchase-util';
import { selectBraintreeEnvironment } from '../../../state/domain/appConfig/appConfigReducer';
import {addDaysToJSDate} from "../bynx/packageUtil";


const checkPackageIsExpiring = (packageEndDate) => {
  if (!packageEndDate) return false;

  const dateIn7days = addDaysToJSDate(new Date(), 7);
  return new Date(packageEndDate) < dateIn7days;
};


/**
 * If the package is expiry soon, this will display a ribbon.
 * @param {object} props The react props
 * @param {string} packageEndDate The end date string.
 */
function ExpiryRibbon({ packageEndDate }) {
  const intl = useIntl();
  const isExpiring = useMemo(() => checkPackageIsExpiring(packageEndDate), [packageEndDate]);
  const expiryDays = useMemo(() => {
    if (!packageEndDate) return null;
    const msTillExpiry = new Date(packageEndDate) - new Date();
    return Math.floor(msTillExpiry / (1000 * 60 * 60 * 24));
  }, [packageEndDate]);

  if (!isExpiring) return <></>;

  return (
    <div className="package-card-ribbon">
      <div title={intl.formatRelativeTime(expiryDays, 'days')}>
        <Icon type="clock-circle" style={{ marginRight: '0.3em' }} />
        Ending Soon
      </div>
    </div>
  );
}

ExpiryRibbon.propTypes = {
  packageEndDate: PropTypes.string,
};

ExpiryRibbon.defaultProps = {
  packageEndDate: null,
};


/**
 * A component to represent a purchasable Token Credit Package.
 * This allows to follow through the select -> purchase -> success/fail process
 * @param {object} props React props
 * @param {object} props.tokenPackage The token package to be displayed.
 * @param {func} props.onSelect A callback function executed when the package has been selected.
 * @param {func} props.onCancel A callback function executed when the selected package is canceled to an unselected state.
 */
export default function CreditPackage({
  creditPackage, onSelect, onCancel, disablePackageSelection,
}) {
  // Redux State
  const chosenPackage = useSelector(selectChosenCreditPackage);
  const selected = chosenPackage && chosenPackage.code === creditPackage.code;
  const visible = !chosenPackage || chosenPackage.code === creditPackage.code;
  const braintreeEnvironment = useSelector(selectBraintreeEnvironment);

  // Redux Dispatch
  const dispatch = useDispatch();
  const makePackagePurchase = nonce => dispatch(requestPackagePurchase(nonce, creditPackage.code));


  // Render - Default interaction rendering.
  return (
    <div className={`package-card ${selected ? 'package-card--selected' : ''} ${visible ? '' : 'package-card--hidden'}`}>
      <ExpiryRibbon packageEndDate={creditPackage.packageEndDate} />
      <div className="package-card-top">
        <div className="package-card-title">
          <Title level={3}>{creditPackage.title}</Title>
        </div>
        <div className="package-card-price">
          {`${CURRENCIES[creditPackage.currencyCode].symbol}${creditPackage.price} ${creditPackage.currencyCode}`}
        </div>
        <div className="package-card-description">
          {creditPackage.description}
        </div>
      </div>
      <div className="package-card-bottom">
        <div className="package-card-tokens">
          <div className="package-card-main-tokens" style={{ visibility: !creditPackage.extraTokens && 'hidden' }}>
            <FormattedMessage id="creditCountMain" values={{ count: creditPackage.mainTokens }} />
          </div>
          <div className="package-card-extra-tokens" style={{ visibility: !creditPackage.extraTokens && 'hidden' }}>
            <FormattedMessage id="creditCountExtra" values={{ count: creditPackage.extraTokens }} />
          </div>
          <div className="package-card-inspections">
            <FormattedMessage id="creditCountTotal" values={{ count: creditPackage.mainTokens + creditPackage.extraTokens }} />
          </div>
        </div>
        <div className="package-card-button">
          <Button disabled={disablePackageSelection} style={{ display: selected && 'none' }} type="primary" onClick={() => { onSelect(creditPackage); }}><FormattedMessage id="select" /></Button>
          {selected && (
            <BrainTreeDropIn
              environment={braintreeEnvironment}
              tokenPackage={creditPackage}
              className="package-purchase"
              onPayPalPayment={makePackagePurchase}
              onPayPalCancel={onCancel}
            />
          )}
        </div>
      </div>
    </div>
  );
}

CreditPackage.propTypes = {
  creditPackage: tokenPackageShape.isRequired,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
  disablePackageSelection: PropTypes.bool,
};

CreditPackage.defaultProps = {
  onSelect: () => {},
  onCancel: () => {},
  disablePackageSelection: false,
};
